import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { usePublishToEmma } from '../hooks/usePublishToEmma';
import axios from 'axios';
import {
  ArrowLeft, Activity, Loader, RefreshCw, Building2, FileText,
  CheckCircle, AlertTriangle, XCircle, TrendingUp, Shield, Lightbulb,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const STATUS_CONFIG = {
  GROEN: { color: '#22c55e', bg: 'rgba(34,197,94,0.13)', icon: CheckCircle, label: 'Goed' },
  ORANJE: { color: '#f59e0b', bg: 'rgba(245,158,11,0.13)', icon: AlertTriangle, label: 'Aandacht' },
  ROOD: { color: '#ef4444', bg: 'rgba(239,68,68,0.13)', icon: XCircle, label: 'Risico' },
};

const TrafficLight = ({ status, size = 14 }) => {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.ORANJE;
  return (
    <span style={{
      display: 'inline-block', width: size, height: size, borderRadius: '50%',
      background: cfg.color, boxShadow: `0 0 6px ${cfg.color}44`,
    }} data-testid={`light-${status}`} />
  );
};

const ScoreRing = ({ score, size = 120, status }) => {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.ORANJE;
  const radius = (size - 12) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (score / 100) * circumference;
  return (
    <div style={{ position: 'relative', width: size, height: size }}>
      <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
        <circle cx={size/2} cy={size/2} r={radius} fill="none" stroke="var(--border-light)" strokeWidth="8" />
        <circle cx={size/2} cy={size/2} r={radius} fill="none" stroke={cfg.color} strokeWidth="8"
          strokeDasharray={circumference} strokeDashoffset={offset} strokeLinecap="round"
          style={{ transition: 'stroke-dashoffset 1s ease' }} />
      </svg>
      <div style={{
        position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center',
      }}>
        <span style={{ fontSize: size * 0.28, fontWeight: 700, color: cfg.color }}>{score}</span>
        <span style={{ fontSize: size * 0.1, color: 'var(--text-secondary)', fontWeight: 500 }}>/100</span>
      </div>
    </div>
  );
};

const BusinessHealthCheck = () => {
  const { token } = useAuth();
  const { lang } = useLanguage();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [hasProfile, setHasProfile] = useState(null);
  const [quickMode, setQuickMode] = useState(false);
  const [quickData, setQuickData] = useState({
    annual_revenue: '', monthly_costs: '', num_employees: '',
    growth_rate: '', sector: '', main_challenges: '',
  });

  
  // Build comprehensive context for Emma
  const emmaContext = React.useMemo(() => {
    if (!data) return null;
    if (data.raw_analysis) return data.raw_analysis;
    const parts = [];
    if (data.overall_score) parts.push(`Overall Score: ${data.overall_score}/100 (Status: ${data.overall_status})`);
    const cats = data.categories || [];
    if (cats.length) {
      parts.push('Categorieen:');
      cats.forEach(c => parts.push(`  - ${c.name}: ${c.score}/100 (${c.status}) — ${c.insight || ''}`));
    }
    if (data.strengths?.length) parts.push('Sterktes:\n' + (Array.isArray(data.strengths) ? data.strengths : []).map(s => `  - ${s}`).join('\n'));
    if (data.risks?.length) parts.push('Risicos:\n' + (Array.isArray(data.risks) ? data.risks : []).map(r => `  - ${r}`).join('\n'));
    if (data.benchmark) parts.push(`Benchmark: ${data.benchmark}`);
    if (data.recommendations?.length) parts.push('Aanbevelingen:\n' + (Array.isArray(data.recommendations) ? data.recommendations : []).map(r => `  - [${r.priority}] ${r.title}: ${r.description}`).join('\n'));
    return (Array.isArray(parts) ? parts : []).join('\n') || null;
  }, [data]);

  usePublishToEmma('AI Business Health Check', emmaContext);
  const headers = { Authorization: `Bearer ${token}` };
  const isNL = lang === 'nl';

  useEffect(() => { checkProfile(); }, []);

  const checkProfile = async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/compass/company/profile`, { headers });
      setHasProfile(!!res.data.profile?.company_name);
    } catch { setHasProfile(false); }
  };

  const runHealthCheck = async () => {
    setLoading(true); setData(null);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/compass/biz-health/check`, { language: lang }, { headers });
      if (!res.data.has_profile) { setHasProfile(false); setLoading(false); return; }
      setData(res.data.data);
    } catch (err) {
      alert(err.response?.data?.detail || 'Check failed');
    } finally { setLoading(false); }
  };

  const runQuickCheck = async () => {
    setLoading(true); setData(null);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/compass/biz-health/quick-check`, {
        annual_revenue: quickData.annual_revenue ? Number(quickData.annual_revenue) : null,
        monthly_costs: quickData.monthly_costs ? Number(quickData.monthly_costs) : null,
        num_employees: quickData.num_employees ? Number(quickData.num_employees) : null,
        growth_rate: quickData.growth_rate ? Number(quickData.growth_rate) : null,
        sector: quickData.sector, main_challenges: quickData.main_challenges, language: lang,
      }, { headers });
      // Quick check returns text, wrap it
      setData({ raw_analysis: res.data.analysis });
    } catch (err) {
      alert(err.response?.data?.detail || 'Check failed');
    } finally { setLoading(false); }
  };

  // Render structured data
  const renderStructured = () => {
    if (!data) return null;
    if (data.raw_analysis) {
      // Fallback markdown rendering
      return <div className="auto-input-card"><div className="markdown-content" style={{ whiteSpace: 'pre-wrap' }}>{data.raw_analysis}</div></div>;
    }

    const cats = data.categories || [];
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }} data-testid="health-result">
        {/* Overall Score */}
        <div className="auto-input-card" style={{ display: 'flex', alignItems: 'center', gap: '2rem', padding: '2rem' }}>
          <ScoreRing score={data.overall_score || 0} status={data.overall_status || 'ORANJE'} size={140} />
          <div>
            <h2 className="heading-2" style={{ marginBottom: '0.25rem' }}>
              {isNL ? 'Totaalscore' : 'Overall Score'}
            </h2>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <TrafficLight status={data.overall_status || 'ORANJE'} size={16} />
              <span className="body-medium" style={{ fontWeight: 600, color: STATUS_CONFIG[data.overall_status]?.color }}>
                {STATUS_CONFIG[data.overall_status]?.label || data.overall_status}
              </span>
            </div>
          </div>
        </div>

        {/* Category Scores */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '1rem' }}>
          {(Array.isArray(cats) ? cats : []).map((cat, i) => {
            const cfg = STATUS_CONFIG[cat.status] || STATUS_CONFIG.ORANJE;
            return (
              <div key={i} className="auto-input-card" style={{
                padding: '1.25rem', borderLeft: `4px solid ${cfg.color}`, background: cfg.bg,
              }} data-testid={`cat-${i}`}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                  <span className="body-small" style={{ fontWeight: 600 }}>{cat.name}</span>
                  <TrafficLight status={cat.status} />
                </div>
                <div style={{ fontSize: '1.75rem', fontWeight: 700, color: cfg.color }}>{cat.score}</div>
                <p className="caption text-secondary" style={{ marginTop: '0.25rem' }}>{cat.detail}</p>
              </div>
            );
          })}
        </div>

        {/* Strengths & Risks */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
          <div className="auto-input-card" style={{ padding: '1.25rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem' }}>
              <TrendingUp size={18} style={{ color: '#22c55e' }} />
              <h3 className="heading-3" style={{ margin: 0 }}>{isNL ? 'Sterke Punten' : 'Strengths'}</h3>
            </div>
            {(data.strengths || []).map((s, i) => (
              <div key={i} style={{ display: 'flex', gap: '0.5rem', padding: '0.4rem 0', borderBottom: '1px solid var(--border-light)' }}>
                <CheckCircle size={14} style={{ color: '#22c55e', flexShrink: 0, marginTop: 2 }} />
                <span className="body-small">{s}</span>
              </div>
            ))}
          </div>
          <div className="auto-input-card" style={{ padding: '1.25rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem' }}>
              <Shield size={18} style={{ color: '#ef4444' }} />
              <h3 className="heading-3" style={{ margin: 0 }}>{isNL ? 'Risico\'s' : 'Risks'}</h3>
            </div>
            {(data.risks || []).map((r, i) => (
              <div key={i} style={{ display: 'flex', gap: '0.5rem', padding: '0.4rem 0', borderBottom: '1px solid var(--border-light)' }}>
                <AlertTriangle size={14} style={{ color: '#ef4444', flexShrink: 0, marginTop: 2 }} />
                <span className="body-small">{r}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Benchmark */}
        {data.benchmark && (
          <div className="auto-input-card" style={{ padding: '1.25rem', background: 'var(--bg-section)' }}>
            <h3 className="heading-3" style={{ marginBottom: '0.5rem' }}>{isNL ? 'Branchevergelijking' : 'Benchmark'}</h3>
            <p className="body-small">{data.benchmark}</p>
          </div>
        )}

        {/* Recommendations */}
        {data.recommendations && (
          <div className="auto-input-card" style={{ padding: '1.25rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem' }}>
              <Lightbulb size={18} style={{ color: '#f59e0b' }} />
              <h3 className="heading-3" style={{ margin: 0 }}>{isNL ? 'Aanbevelingen' : 'Recommendations'}</h3>
            </div>
            {(data.recommendations || []).map((r, i) => (
              <div key={i} style={{
                padding: '0.75rem', marginBottom: '0.5rem', borderRadius: '0.5rem',
                background: r.urgency === 'hoog' ? '#16202C' : r.urgency === 'medium' ? '#16202C' : '#16202C',
                borderLeft: `3px solid ${r.urgency === 'hoog' ? '#ef4444' : r.urgency === 'medium' ? '#f59e0b' : '#22c55e'}`,
              }}>
                <div className="body-small" style={{ fontWeight: 600 }}>{r.title}</div>
                <div className="caption text-secondary">{r.description}</div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="tool-page">
      <div className="tool-header">
        <button onClick={() => navigate('/portal/ai-tools')} className="btn-secondary" data-testid="back-btn">
          <ArrowLeft size={16} /> AI Tools
        </button>
        <h1 className="heading-1">
          <Activity size={22} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 8 }} />
          {isNL ? 'Bedrijfs Gezondheidscheck' : 'Business Health Check'}
        </h1>
      </div>
      <div className="tool-content" style={{ maxWidth: '900px' }}>
        <p className="body-medium text-secondary" style={{ marginBottom: '1.5rem' }}>
          {isNL ? 'Laat AI je bedrijf doorlichten met verkeerslicht-scores per categorie' : 'Let AI scan your business with traffic light scores per category'}
        </p>

        {!data && !loading && (
          <div className="auto-start-section" data-testid="health-start">
            {hasProfile ? (
              <div className="auto-input-card">
                <button className="btn-primary" style={{ width: '100%' }}
                  onClick={runHealthCheck} data-testid="run-check-btn">
                  <Activity size={16} /> {isNL ? 'Start Gezondheidscheck' : 'Run Health Check'}
                </button>
              </div>
            ) : hasProfile === false ? (
              <div className="auto-input-card">
                <p className="body-small text-secondary">{isNL ? 'Vul eerst je bedrijfsprofiel in.' : 'Fill in your company profile first.'}</p>
                <button className="btn-primary" style={{ width: '100%', marginTop: '1rem' }}
                  onClick={() => navigate('/portal/company-profile')} data-testid="go-profile-btn">
                  <Building2 size={16} /> {isNL ? 'Vul Bedrijfsprofiel In' : 'Fill Company Profile'}
                </button>
                <button className="btn-secondary" style={{ width: '100%', marginTop: '0.75rem' }}
                  onClick={() => setQuickMode(true)} data-testid="quick-mode-btn">
                  {isNL ? 'Of doe een snelle check' : 'Or do a quick check'}
                </button>
              </div>
            ) : null}
            {quickMode && (
              <div className="auto-input-card" data-testid="quick-form">
                <h3 className="heading-3">{isNL ? 'Snelle Check' : 'Quick Check'}</h3>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', marginTop: '1rem' }}>
                  {[['annual_revenue', isNL ? 'Jaaromzet' : 'Revenue'], ['monthly_costs', isNL ? 'Maandkosten' : 'Costs'], ['num_employees', isNL ? 'Medewerkers' : 'Employees'], ['growth_rate', isNL ? 'Groei %' : 'Growth %']].map(([k, l]) => (
                    <div className="form-group" key={k}>
                      <label className="body-small" style={{ fontWeight: 600 }}>{l}</label>
                      <input type="number" className="form-input" value={quickData[k]}
                        onChange={(e) => setQuickData({...quickData, [k]: e.target.value})} />
                    </div>
                  ))}
                </div>
                <button className="btn-primary" style={{ width: '100%', marginTop: '0.75rem' }}
                  onClick={runQuickCheck} data-testid="quick-analyze-btn">
                  <Activity size={16} /> {isNL ? 'Analyseer' : 'Analyze'}
                </button>
              </div>
            )}
          </div>
        )}

        {loading && (
          <div className="auto-input-card" style={{ textAlign: 'center', padding: '3rem' }}>
            <Loader className="spin" size={24} style={{ margin: '0 auto 1rem' }} />
            <p className="body-medium">{isNL ? 'AI analyseert je bedrijf...' : 'AI is analyzing...'}</p>
          </div>
        )}

        {data && renderStructured()}

        {data && (
          <button className="btn-secondary" style={{ marginTop: '1.5rem' }}
            onClick={() => { setData(null); setQuickMode(false); }} data-testid="new-check-btn">
            <RefreshCw size={16} /> {isNL ? 'Nieuwe Check' : 'New Check'}
          </button>
        )}
      </div>
    </div>
  );
};

export default BusinessHealthCheck;
